import React from 'react';
import { VerificationEntry } from '../../types';
import { XCircle } from 'lucide-react';

interface DetailModalProps {
	entry: VerificationEntry;
	onClose: () => void;
	formatDate: (date: Date) => string;
	isLoading?: boolean;
}

const DetailModal: React.FC<DetailModalProps> = ({ entry, onClose, formatDate, isLoading = false }) => {
	return (
		<div className="fixed inset-0 bg-black/70 flex justify-center items-center z-50 p-4 overflow-y-auto">
			<div className="bg-white rounded-xl shadow-xl p-8 w-full max-w-6xl relative animate-fadeIn">
				{/* Loading Overlay */}
				{isLoading && (
					<div className="absolute inset-0 bg-white/90 flex items-center justify-center rounded-xl z-10">
						<div className="flex flex-col items-center gap-3">
							<div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
							<p className="text-gray-600 font-medium">Loading full details...</p>
						</div>
					</div>
				)}
				<button
					onClick={onClose}
					className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
				>
					<XCircle className="w-6 h-6" />
				</button>

				<h3 className="text-2xl font-bold text-gray-800 mb-8 border-b pb-4">
					Verification Summary
				</h3>
				<div className="flex flex-col md:flex-row gap-10">
					{/* Images Column */}
					<div className="flex flex-col items-center gap-8 md:w-2/5 w-full">
						{(entry.selfie_image_url || entry.selfie_image) && (
							<div className="flex flex-col items-center">
								<img
									src={entry.selfie_image_url || `data:image/jpeg;base64,${entry.selfie_image}`}
									alt="Selfie"
									className="w-72 h-72 object-cover rounded-lg border-2 border-gray-200 shadow-md mb-3 hover:scale-105 transition-transform duration-200"
								/>
								<span className="text-sm font-medium text-gray-600">Submitted Selfie</span>
							</div>
						)}
						{(entry.biometric_face_image_url || entry.biometricFaceImage) && (
							<div className="flex flex-col items-center">
								<img
									src={
										entry.biometric_face_image_url ||
										`data:image/png;base64,${entry.biometricFaceImage}`
									}
									alt="Biometric Face"
									className="w-72 h-72 object-cover rounded-lg border-2 border-gray-200 shadow-md mb-3 hover:scale-105 transition-transform duration-200"
								/>
								<span className="text-sm font-medium text-gray-600">
									Biometric Face (Ghana Card)
								</span>
							</div>
						)}
					</div>

					{/* Details Column */}
					<div className="flex-1 space-y-3 text-base text-gray-700 bg-gray-50 p-6 rounded-lg shadow-inner">
						{[
							['Ghana Card ID', entry.ghanaCardId, 'font-mono'],
							['Full Name', `${entry.firstName || 'N/A'} ${entry.lastName || ''}`],
							['Date of Birth', entry.dateOfBirth],
							['Card Valid From', entry.card_valid_from],
							['Card Valid To', entry.card_valid_to],
							[
								'Status',
								<span
									className={`px-3 py-1.5 rounded-full text-sm font-semibold inline-block ${
										entry.status === 'verified'
											? 'bg-green-100 text-green-700'
											: entry.status === 'failed'
											? 'text-red-700'
											: 'text-yellow-700'
									}`}
								>
									{entry.status}
								</span>,
							],
							['Birth Town', entry.birth_town],
							['Birth Region', entry.birth_region],
							['GPS Address', entry.gps_address],
							['Address', entry.address],
							['Town', entry.town],
							['Region', entry.region],
							['Country', entry.country],
						]
							.filter(([_, value]) => value)
							.map(([label, value, className = '']) => (
								<div
									key={label as string}
									className="grid grid-cols-3 gap-3 py-2 border-b border-gray-100 last:border-0"
								>
									<span className="text-gray-600 font-medium col-span-1">{label}:</span>
									<span className={`${(className as string) || ''} font-semibold col-span-2`}>
										{value}
									</span>
								</div>
							))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default DetailModal;
